import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { getSupportQueue, RoutingHint, SupportQueueRow } from '../api/support';
import {
  Empty,
  FilterBar,
  Kpi,
  KpiRow,
  Note,
  OpsPage,
  Pill,
  PillTone,
  Row,
  SlaTag,
  Table,
} from '../../../design-system/ops';
import { PLATFORM_CURRENCY, formatMoney } from '../../../commodity/format/money';

/**
 * PI-3 Epic 3 S3.5 + S3.7 — open support cases with their routing hints.
 *
 * Rewritten onto the design system. It previously drew its own chip with its
 * own three colours (`#2b8a3e`, `#1971c2`, `#c92a2a`) and its own amber border
 * for a breached SLA — none of which the console's theme reached, and none of
 * which agreed with how the rest of the console shows the same two facts.
 *
 * The SLA column is now `SlaTag`, which is the component that decides late,
 * due-soon and on-time for every surface. Two screens disagreeing about whether
 * a case is overdue is the kind of thing an operator notices and a test does
 * not — and this screen and the operations queue show overlapping work.
 */

const HINT_TONE: Record<RoutingHint, PillTone> = {
  auto_resolvable: 'ok',
  semimanual: 'tint',
  escalate: 'bad',
};

const HINT_LABEL: Record<RoutingHint, string> = {
  auto_resolvable: 'Automático',
  semimanual: 'Semimanual',
  escalate: 'Escalar',
};

type Filter = 'all' | RoutingHint;

const AdminSupportQueue: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<SupportQueueRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getSupportQueue();
        if (!cancelled) setRows(res.rows ?? []);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'No se pudo cargar la cola de soporte');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const shown = useMemo(
    () => (rows ?? []).filter((r) => filter === 'all' || r.routing.hint === filter),
    [rows, filter],
  );

  if (error) {
    return (
      <OpsPage title="Cola de soporte">
        <Note tone="bad">{error}</Note>
      </OpsPage>
    );
  }

  if (!rows) {
    return (
      <OpsPage title="Cola de soporte">
        <Note>Cargando…</Note>
      </OpsPage>
    );
  }

  const breaching = rows.filter((r) => r.routing.slaBreached).length;
  const money = rows.reduce((sum, r) => sum + (r.moneyAtStakeUsd ?? 0), 0);

  return (
    <OpsPage
      title="Cola de soporte"
      subtitle="Casos abiertos, con la sugerencia de enrutamiento y su reloj de SLA."
    >
      <KpiRow>
        <Kpi
          label="Incumpliendo SLA"
          value={breaching}
          note={breaching ? 'Atender primero' : 'Nada vencido'}
          tone={breaching ? 'bad' : 'neutral'}
          alert={breaching > 0}
        />
        <Kpi label="Casos abiertos" value={rows.length} note="En total" />
        <Kpi
          label="Dinero en juego"
          value={formatMoney(money, PLATFORM_CURRENCY)}
          note="Congelado"
          tone={money > 0 ? 'lav' : 'neutral'}
        />
        <Kpi
          label="Para escalar"
          value={rows.filter((r) => r.routing.hint === 'escalate').length}
          note="Necesitan especialista"
          tone="warn"
        />
      </KpiRow>

      <FilterBar<Filter>
        label="Filtrar por enrutamiento"
        value={filter}
        onChange={setFilter}
        options={[
          { value: 'all', label: 'Todos' },
          { value: 'auto_resolvable', label: HINT_LABEL.auto_resolvable },
          { value: 'semimanual', label: HINT_LABEL.semimanual },
          { value: 'escalate', label: HINT_LABEL.escalate },
        ]}
      />

      {shown.length === 0 ? (
        <Empty
          title="Nada en esta vista"
          detail={
            filter === 'all'
              ? 'La cola de soporte está limpia.'
              : 'Ningún caso abierto con ese enrutamiento.'
          }
        />
      ) : (
        <Table columns="120px 1fr 110px 120px" head={['Enrutamiento', 'Caso', 'Importe', 'SLA']}>
          {shown.map((r) => (
            <Row
              key={r.caseId}
              onOpen={() => navigate(`/admin/support/case/${r.enrollmentId}`)}
              label={`Abrir el caso ${r.caseId}`}
            >
              <Pill tone={HINT_TONE[r.routing.hint]}>{HINT_LABEL[r.routing.hint]}</Pill>
              <span>
                <b>{r.summary}</b>
                <br />
                <span className="mut">{r.routing.reason}</span>
              </span>
              <span>
                {r.moneyAtStakeUsd ? formatMoney(r.moneyAtStakeUsd, PLATFORM_CURRENCY) : '—'}
              </span>
              {/* The hours come from the server, which computed them from the
                  record's timestamp against the governed policy. A countdown
                  run here would not satisfy ADM-02-FR-04. */}
              <SlaTag hoursRemaining={r.slaHours - r.elapsedHours} dueSoonHours={r.dueSoonHours} />
            </Row>
          ))}
        </Table>
      )}
    </OpsPage>
  );
};

export default AdminSupportQueue;
